/**
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart
 * (for umati and VDW e.V.)
 */

import { logout } from 'api/auth';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUser } from 'redux/actions';
import { IStore } from 'redux/storeType';
/**
 * Account page showing the currently logged-in user and a logout button.
 *
 * The page waits until the Redux user state has been initialized before
 * deciding whether the user should be redirected to /login.
 */
function Account() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // IMPORTANT:
  // Use separate selectors instead of returning an object from one selector.
  // This prevents:
  // "Selector returned a different result when called with the same parameters."
  const currentUser = useSelector((state: IStore) => state.user?.CurrentUser);
  const initialized = useSelector((state: IStore) => state.user?.initialized);
  /**
   * Redux is still restoring/checking the user's session.
   *
   * Do NOT redirect to /login yet.
   */
  if (!initialized) {
    return (
      <main className="container">
        <div className="row vh-100 d-flex justify-content-center align-items-center">
          <div className="col-12 col-md-8 col-lg-6 text-center">
            <div className="card shadow">
              <div className="card-body">
                <p className="mb-0">Loading...</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  /**
   * Redux has finished initializing and there is no logged-in user.
   */
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  /**
   * Logout the user.
   *
   * Even if the backend logout fails, we still clear the local
   * Redux session and redirect to login.
   */
  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Backend logout failed.
      // We still clear the local Redux session below.
    }

    dispatch(setCurrentUser(undefined));

    navigate('/login', { replace: true });
  };

  return (
    <main className="container">
      <div className="row vh-100 d-flex justify-content-center align-items-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="border border-3 border-primary" />

          <div className="card shadow">
            <div className="card-body">
              <div className="mb-3 mt-md-4">
                <h2 className="fw-bold mb-2">Account</h2>

                <p className="mb-3">
                  Logged in as <strong>{currentUser.Username}</strong>
                </p>

                <button type="button" className="btn btn-primary" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Account;
